import { Navigate, Route, Routes } from 'react-router-dom';
import { useMe } from '../api/queries.ts';
import { ApiError } from '../api/client.ts';
import { LoginPage, SignupPage, InvitePage, ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from '../pages/Auth.tsx';
import { HomePage } from '../pages/Home.tsx';
import { TasksPage } from '../pages/Tasks.tsx';
import { InboxPage } from '../pages/Inbox.tsx';
import { ChatPage } from '../pages/Chat.tsx';
import { WorkspacePage } from '../pages/Workspace.tsx';
import { CrmAppPage } from '../pages/Crm.tsx';
import { MenuPage } from '../pages/Menu.tsx';
import { SettingsPage } from '../pages/Settings.tsx';
import { OrgLayout } from './OrgLayout.tsx';
import { CalendarPage } from '../features/calendar/CalendarPage.tsx';
import { PageLoader, EmptyState } from '../ui/primitives.tsx';
import { lastOrg } from '../lib/theme.ts';
import { usePushNavigation } from '../lib/push.ts';

export function App() {
  const me = useMe();
  usePushNavigation();
  const loggedOut = me.error instanceof ApiError && me.error.status === 401;

  if (me.isLoading) return <PageLoader />;

  if (!me.data) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/invite/:token" element={<InvitePage me={me} />} />
        <Route path="*" element={loggedOut ? <Navigate to="/login" replace /> : <div className="page"><EmptyState tone="neutral" title="Unitly non è raggiungibile">{me.error?.message}</EmptyState></div>} />
      </Routes>
    );
  }

  // Blocks the whole app until the signup verification code is confirmed - accounts that existed before
  // this feature shipped were grandfathered as already verified, so this only ever gates brand-new signups.
  if (!me.data.user.emailVerified) return <VerifyEmailPage email={me.data.user.email} />;

  const orgs = me.data.organizations;
  const home = orgs.find((o) => o.slug === lastOrg()) ?? orgs[0];

  return (
    <Routes>
      <Route path="/invite/:token" element={<InvitePage me={me} />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/signup" element={<Navigate to="/" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/" replace />} />
      <Route path="/o/:orgSlug" element={<OrgLayout me={me.data} />}>
        <Route index element={<HomePage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="chat/:channelId" element={<ChatPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="w/:wsKey" element={<WorkspacePage />} />
        <Route path="w/:wsKey/crm" element={<CrmAppPage section="pipeline" />} />
        <Route path="w/:wsKey/crm/companies" element={<CrmAppPage section="companies" />} />
        <Route path="w/:wsKey/crm/contacts" element={<CrmAppPage section="contacts" />} />
        <Route path="w/:wsKey/crm/report" element={<CrmAppPage section="report" />} />
        <Route path="menu" element={<MenuPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/:section" element={<SettingsPage />} />
      </Route>
      <Route
        path="*"
        element={
          home ? <Navigate to={`/o/${home.slug}`} replace /> : (
            <div className="auth"><div className="auth-card">
              <EmptyState tone="neutral" title="Nessuna organizzazione">Il tuo account non fa parte di nessuna organizzazione attiva. Apri il link di invito che hai ricevuto.</EmptyState>
            </div></div>
          )
        }
      />
    </Routes>
  );
}
